// src/pages/admin/recurringRules/helpers.js — pure date/rule/assignment-doc-id helpers used
// by RecurringRulesTab. No React state; every function is a function of its arguments only.
import { serverTimestamp } from "firebase/firestore";

export const ASSIGNMENT_COLLECTION_CANDIDATES = [
  "assignments",
  "dailyAssignments",
  "assignmentDocuments",
  "assignmentDocs",
  "routeAssignments",
];

export const RULE_LINK_FIELD_CANDIDATES = ["recurrenceRuleId", "recurringRuleId", "ruleId"];
export const ASSIGNMENT_DATE_FIELD_CANDIDATES = ["date", "runDate", "assignmentDate", "scheduledDate"];

export function isYYYYMMDD(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
}

export function compareYYYYMMDD(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function todayYYYYMMDD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function maxYYYYMMDD(a, b) {
  if (!a) return b || "";
  if (!b) return a || "";
  return a >= b ? a : b;
}

export function pickFirstExistingField(obj, candidates) {
  for (const f of candidates) {
    if (obj && obj[f] != null && obj[f] !== "") return f;
  }
  return "";
}

export function isNotCompletedAssignment(a) {
  if (!a) return true;
  if ("completedAt" in a) return !a.completedAt;
  if ("isCompleted" in a) return !a.isCompleted;
  if ("status" in a) return String(a.status).toLowerCase() !== "completed";
  if ("assignmentStatus" in a) return String(a.assignmentStatus).toLowerCase() !== "completed";
  return true;
}

export function countTemplate(runsTemplate) {
  const runsObj = runsTemplate || {};
  const keys = Object.keys(runsObj);
  let stopCount = 0;
  keys.forEach((k) => {
    const list = runsObj[k];
    if (Array.isArray(list)) stopCount += list.length;
  });
  return { runCount: keys.length, stopCount };
}

export function fmtDate(dateStr) {
  if (!dateStr) return "";
  return String(dateStr);
}

export function getRuleCompanyId(rule) {
  return (
    rule.companyId ||
    rule.companyID ||
    rule.company?.id ||
    rule.company?.companyId ||
    rule.companyRefId ||
    rule.companyRef ||
    ""
  );
}

export function getRuleCompanyIds(rule) {
  if (Array.isArray(rule?.targets) && rule.targets.length) {
    return rule.targets.map((t) => t?.companyId).filter(Boolean);
  }
  const single = getRuleCompanyId(rule);
  return single ? [single] : [];
}

export function getRuleTemplateCounts(rule) {
  if (Array.isArray(rule?.targets) && rule.targets.length) {
    let runCount = 0;
    let stopCount = 0;
    rule.targets.forEach((t) => {
      const c = countTemplate(t?.runsTemplate);
      runCount += c.runCount;
      stopCount += c.stopCount;
    });
    return { runCount, stopCount };
  }
  return countTemplate(rule?.runsTemplate);
}

export function getRuleDriverId(rule) {
  return rule.driverId || rule.driverUid || rule.driver?.uid || rule.driver?.id || "";
}

export function getRuleDriverEmail(rule) {
  return rule.driverEmail || rule.driver?.email || rule.email || "";
}

export function buildAssignmentDriverPatch({ newDriverUid, newDriverEmail }) {
  const emailLower = newDriverEmail ? String(newDriverEmail).toLowerCase() : null;

  return {
    updatedAt: serverTimestamp(),
    reassignedByRuleAdminAt: serverTimestamp(),
    driverId: newDriverUid || null,
    driverUid: newDriverUid || null,
    driverEmail: newDriverEmail || null,
    driverEmailLower: emailLower,
    assignedDriverId: newDriverUid || null,
    assignedDriverUid: newDriverUid || null,
    assignedDriverEmail: newDriverEmail || null,
    assignedTo: newDriverUid || null,
    assignedToUid: newDriverUid || null,
    assignedToEmail: newDriverEmail || null,
  };
}

export function patchNestedRunsIfAny(assignmentData, newDriverUid, newDriverEmail) {
  if (!assignmentData) return null;

  if (assignmentData.runs && typeof assignmentData.runs === "object") {
    return null;
  }

  if (Array.isArray(assignmentData.jobs)) {
    const emailLower = newDriverEmail ? String(newDriverEmail).toLowerCase() : null;

    const jobs = assignmentData.jobs.map((j) => ({
      ...j,
      driverId: newDriverUid || j.driverId || null,
      driverUid: newDriverUid || j.driverUid || null,
      driverEmail: newDriverEmail || j.driverEmail || null,
      driverEmailLower: emailLower,
      assignedDriverId: newDriverUid || j.assignedDriverId || null,
      assignedDriverEmail: newDriverEmail || j.assignedDriverEmail || null,
      assignedTo: newDriverUid || j.assignedTo || null,
      assignedToEmail: newDriverEmail || j.assignedToEmail || null,
    }));
    return { jobs };
  }

  return null;
}

export function parseAssignmentDocId(id) {
  const s = String(id || "");
  if (!s) return null;

  const parts = s.split("_");
  if (parts.length < 2) return null;

  const last = parts[parts.length - 1];
  if (!isYYYYMMDD(last)) return null;

  if (parts.length === 2) {
    return { scheme: "driver_date", driverUidFromId: parts[0], companyIdFromId: "", dateFromId: last };
  }

  if (parts.length >= 3) {
    const driverUidFromId = parts[parts.length - 2];
    const companyIdFromId = parts.slice(0, parts.length - 2).join("_");
    return { scheme: "company_driver_date", driverUidFromId, companyIdFromId, dateFromId: last };
  }

  return null;
}

export function buildAssignmentDocIdLike(oldId, newDriverUid) {
  const parsed = parseAssignmentDocId(oldId);
  if (!parsed) return "";

  if (parsed.scheme === "driver_date") {
    return `${newDriverUid}_${parsed.dateFromId}`;
  }
  if (parsed.scheme === "company_driver_date") {
    return `${parsed.companyIdFromId}_${newDriverUid}_${parsed.dateFromId}`;
  }
  return "";
}
